import React, { Component, Fragment } from 'react';
import { toastr } from 'react-redux-toastr';
import styled from 'styled-components';
import {
  has,
  set,
  get,
  find,
  map,
  size,
  reject,
  isEqual,
  keyBy,
  isEmpty
} from 'lodash/fp';
import PropTypes from 'prop-types';
import {
  Alert,
  Schema,
  Button,
  FormControl,
  Form,
  Icon,
  Input,
  Tag,
  InputGroup,
  HelpBlock
} from 'rsuite';
import {
  InventoryLocationType,
  InventoryLocationZoneType
} from '../../../interfaces/inventory';
import DeleteConfirmationModal from '../../DeleteConfirmationModal';
import { ControlLabel, FormGroup } from '../../widgets/FormElements';

const { StringType } = Schema.Types;
const FormModel = Schema.Model({
  name: StringType().isRequired('This field is required')
});

export const ZoneList = styled.div`
  float: right;
  display: inline-block;
  margin-right: 18px;
  margin-top: 10px;
`;

export const ZoneItem = styled(Tag)`
  display: block !important;
  margin-bottom: 5px;
  margin-left: 0 !important;
  width: 280px !important;
`;

export default class InventoryLocationForm extends Component {
  static propTypes = {
    location: InventoryLocationType,
    zones: PropTypes.arrayOf(InventoryLocationZoneType),
    isSaving: PropTypes.bool.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onDeleteZone: PropTypes.func.isRequired,
    isDeletingZone: PropTypes.bool.isRequired,
    isDeletedZone: PropTypes.bool.isRequired
  };

  static defaultProps = {
    location: null,
    zones: []
  };

  constructor(props) {
    super(props);
    const { location, zones } = props;
    this.state = {
      formError: {},
      formValue: location || { name: '' },
      zones: zones || [],
      zoneInputValue: '',
      showConfirmDeleteZone: false,
      zoneToDelete: null
    };
    this.formRef = React.createRef();
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleZoneInputChange = this.handleZoneInputChange.bind(this);
  }

  componentDidUpdate({ isDeletedZone: prevIsDeletedZone }) {
    const { isDeletedZone } = this.props;
    if (!prevIsDeletedZone && isDeletedZone) {
      this.handleHideDeleteZoneModal();
    }
  }

  static getDerivedStateFromProps(props, state) {
    if (!isEqual(props.zones, state.zones)) {
      const zonesByName = keyBy('name', props.zones);
      const filteredZones = reject(
        zone => has('id', zone) || has(zone.name, zonesByName)
      )(state.zones);
      return set('zones', [...filteredZones, ...props.zones], state);
    }
    return state;
  }

  get hasChange() {
    const { location, zones: propZones } = this.props;
    const { formValue, zones: stateZones } = this.state;
    return (
      get('name', formValue) !== get('name', location) ||
      !isEqual(propZones, stateZones)
    );
  }

  handleFormChange = data => {
    const { formValue } = this.state;
    this.setState({ formValue: { ...formValue, ...data } });
  };

  handleSubmit = () => {
    if (!this.formRef.current.check()) {
      return;
    }

    const { onSubmit } = this.props;
    const { formValue, zones } = this.state;
    onSubmit(formValue, zones);
  };

  handleReset = () => {
    this.setState({ formValue: { name: '' }, zones: [], zoneInputValue: '' });
  };

  handleRemoveZone = zone => {
    const { zones } = this.props;
    if (size(zones) === 1) {
      const oneZoneMsg = `Zone cannot be deleted. Location must have at least 1 Zone.`;
      Alert.error(oneZoneMsg, 5000);
      return;
    }
    if (zone.id) {
      this.setState({ zoneToDelete: zone });
      this.handleOpenDeleteZoneModal();
    } else {
      this.removeZone(zone);
    }
  };

  removeZone = zone => {
    const { zones: currentZones } = this.state;
    const zones = reject({ name: zone.name }, currentZones);
    this.setState({ zones });
  };

  handleAddZone = () => {
    const { zones, zoneInputValue: name } = this.state;
    const zoneExists = find({ name }, zones);
    if (!zoneExists) {
      const nextZones = name ? [{ name }, ...zones] : zones;
      this.setState({ zones: nextZones, zoneInputValue: '' });
    } else {
      setTimeout(() => {
        toastr.error('Error', 'Zone already exists');
      }, 100);
    }
  };

  handleOpenDeleteZoneModal = () => {
    this.setState({ showConfirmDeleteZone: true });
  };

  handleHideDeleteZoneModal = () => {
    this.setState({ showConfirmDeleteZone: false });
  };

  handlConfirmDeleteZone = () => {
    const { onDeleteZone } = this.props;
    const { zoneToDelete } = this.state;
    onDeleteZone(zoneToDelete);
  };

  handleZoneInputChange(zoneInputValue) {
    this.setState({ zoneInputValue });
  }

  renderZoneInput() {
    const { zoneInputValue } = this.state;
    return (
      <InputGroup style={{ width: 300 }}>
        <Input
          placeholder="Type zone here"
          style={{ paddingLeft: 10 }}
          value={zoneInputValue}
          onChange={this.handleZoneInputChange}
          onPressEnter={this.handleAddZone}
        />
        <InputGroup.Button
          style={{ borderBottom: 'none' }}
          disabled={!zoneInputValue}
          onClick={this.handleAddZone}
        >
          <Icon icon="plus" />
        </InputGroup.Button>
      </InputGroup>
    );
  }

  renderZones() {
    const { zones } = this.state;
    return (
      <ZoneList>
        {map(
          zone => (
            <ZoneItem
              {...(zone.id ? { color: 'blue' } : {})}
              key={zone.name}
              closable
              onClose={() => {
                this.handleRemoveZone(zone);
              }}
            >
              {zone.name}
            </ZoneItem>
          ),
          zones
        )}
      </ZoneList>
    );
  }

  render() {
    const {
      zones,
      formValue,
      formError,
      showConfirmDeleteZone,
      zoneToDelete
    } = this.state;
    const { isSaving, isDeletingZone } = this.props;
    return (
      <Fragment>
        <Form
          style={{ maxWidth: 500 }}
          layout="horizontal"
          model={FormModel}
          ref={this.formRef}
          formValue={formValue}
          onChange={this.handleFormChange}
          onCheck={error => {
            this.setState({ formError: error });
          }}
        >
          <FormGroup>
            <ControlLabel>Name *</ControlLabel>
            <FormControl
              name="name"
              errorMessage={formError.name}
              style={{ paddingLeft: 10 }}
            />
          </FormGroup>
          <FormGroup>
            <ControlLabel>Zone(s) *</ControlLabel>
            {this.renderZoneInput()}
            <HelpBlock style={{ display: 'inline-block', margin: 3 }}>
              <small>
                <Icon icon="info" /> Click + or press enter to add zone
              </small>
            </HelpBlock>
            {this.renderZones()}
          </FormGroup>

          <FormGroup style={{ textAlign: 'right' }}>
            <Button
              style={{ marginRight: 18 }}
              onClick={this.handleSubmit}
              appearance="primary"
              disabled={isSaving || !this.hasChange || isEmpty(zones)}
            >
              <Icon icon="save" /> {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </FormGroup>
        </Form>
        {zoneToDelete && (
          <DeleteConfirmationModal
            message={`Are you sure you want to delete zone "${
              zoneToDelete.name
            }"`}
            show={showConfirmDeleteZone}
            onHide={this.handleHideDeleteZoneModal}
            onConfirm={this.handlConfirmDeleteZone}
            isDeleting={isDeletingZone}
          />
        )}
      </Fragment>
    );
  }
}
